import { useCallback, useState } from 'react';
import { AppContextType } from 'marketplace-ui-common/types';
import { ReviewType } from '../types';
import { useGetPreviousReviews } from './useGetPreviousReviews';
import { useReviewability } from './useReviewability';

interface UseReviewFlowProps {
  appContext: AppContextType;
  entityId: number;
  entityPortalId: number;
  isPreview?: boolean;
  reviewType: ReviewType;
  reviewerUserId?: number;
  skip?: boolean;
}

export type UseReviewFlowReturn = ReturnType<typeof useReviewFlow>;

/**
 * Bundles reviewability checks and previous review fetching into a single hook.
 * Use directly when only one component needs review flow data.
 *
 * When multiple sibling components need the same review flow data,
 * use ReviewFlowProvider + useReviewFlowContext from contexts/ReviewFlowContext instead.
 */
export function useReviewFlow({
  appContext,
  entityId,
  entityPortalId,
  isPreview,
  reviewType,
  reviewerUserId,
  skip,
}: UseReviewFlowProps) {
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const isPublicApp = appContext === 'public' || !!isPreview;

  const {
    reviews: editableReviews,
    reviewsFetchStatus: editedReviewFetchStatus,
  } = useGetPreviousReviews({
    appContext,
    entityId,
    reviewType,
    reviewerUserId,
    skip: skip || !reviewerUserId,
  });
  const editedReview = editableReviews[0];

  const { unreviewableReason, requestState: reviewabilityRequestStatus } =
    useReviewability({
      editPending: !!editedReview && reviewSubmitted,
      entityId,
      entityPortalId,
      isPublicApp,
      reviewPending: !editedReview && reviewSubmitted,
      reviewType,
      skip,
    });

  const hasEditableReview = !isPublicApp && !!editedReview;

  const onReviewSubmitted = useCallback(() => {
    setReviewSubmitted(true);
  }, []);

  return {
    editedReview,
    editedReviewFetchStatus,
    hasEditableReview,
    onReviewSubmitted,
    reviewabilityRequestStatus,
    reviewSubmitted,
    unreviewableReason,
  };
}
